import { Button, Result, Typography } from 'antd';
import React from 'react';

const { Paragraph, Text } = Typography;

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * 渲染期抛异常时的兜底。
 *
 * 之前 `src` 下没有任何 error boundary，所以任意一处渲染错误都会把整个应用白掉，
 * 只能 F5。实际踩到的是"某个站上游探测失败 → 后端发 balance: null → 表格里
 * `balance.toFixed()` 抛 TypeError → 整页消失"。单点的 null 判断当然要修，但
 * "一个格子渲染失败就丢掉整个界面"本身就是不该有的放大效应。
 *
 * 这里只截住渲染错误（React 的 error boundary 不接管事件回调和异步里的异常），
 * 并把错误原文显示出来——白屏最难受的地方是连线索都没有。
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // 留在控制台里，方便对着堆栈定位；不上报到任何外部服务。
    console.error('界面渲染出错:', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    return (
      <Result
        status="error"
        title="界面渲染出错"
        subTitle="这一处渲染失败了，但其他功能还在。可以先重试，或刷新页面。"
        extra={[
          <Button type="primary" key="retry" onClick={this.handleReset}>
            重试
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            刷新页面
          </Button>,
        ]}
      >
        <Paragraph>
          <Text code copyable={{ text: `${error.message}\n${error.stack ?? ''}` }}>
            {error.message}
          </Text>
        </Paragraph>
      </Result>
    );
  }
}

export default ErrorBoundary;
